'use client';

import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faEnvelope, faArrowRight } from '@fortawesome/free-solid-svg-icons';

const FAQS = [
  {
    q: 'Do I need a credit card to start?',
    a: 'No. The free plan is free forever and requires no payment details. Paid trials also start without a credit card — you only enter billing information when you choose to upgrade.',
  },
  {
    q: 'What counts as an "AI call"?',
    a: 'One AI call is one AI operation — for example, translating your app description to all 28 locales in one click counts as 1 call, not 28. Generating an AI review reply or running an AI Marketing generation also counts as 1 call. OrbAI chat messages do not count toward your AI call limit.',
  },
  {
    q: 'Can I connect both App Store and Play Store on the free plan?',
    a: "Yes — but only for 1 app total across both platforms. If you want to manage separate iOS and Android apps, you'll need to upgrade to Solo or higher.",
  },
  {
    q: 'How do you store my App Store Connect and Play Console credentials?',
    a: 'Your API keys and credentials are encrypted with AES-256-GCM before being stored, and are only decrypted in memory during API calls. We never log or share your credentials.',
  },
  {
    q: 'What happens when I hit my AI call limit?',
    a: "You'll see a clear in-app warning at 80% usage. When you reach 100%, AI features are paused until the next billing cycle — no surprise charges. You can upgrade at any time to immediately restore access.",
  },
  {
    q: 'Can I cancel or downgrade at any time?',
    a: 'Yes, absolutely. Cancel or downgrade from your account settings at any time. If you cancel a paid plan, you keep full access until the end of your current billing period. No cancellation fees.',
  },
  {
    q: 'Do you offer refunds?',
    a: "Yes. If you're not satisfied within 30 days of upgrading to a paid plan, we'll refund you — no questions asked. Just email contact@apporb.net.",
  },
  {
    q: 'Can I add team members?',
    a: 'Team seats are available on the Studio plan (3 seats) and Agency plan (10 seats). Each seat holder gets their own login and access to the shared workspace.',
  },
  {
    q: 'Does Apporb work for both iOS and Android apps?',
    a: 'Yes. Apporb integrates with Apple App Store Connect and Google Play Console. You can manage iOS and Android versions of the same app side-by-side.',
  },
  {
    q: 'Is there an onboarding guide or documentation?',
    a: 'We provide a step-by-step getting-started checklist inside the app, plus a help center with guides for connecting credentials, localizing listings, and building screenshots. Email support is available on all paid plans.',
  },
];

function FAQItem({ item, isOpen, onToggle }: {
  item: typeof FAQS[0];
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className={`rounded-2xl overflow-hidden border transition-all ${isOpen ? 'border-[#bfdbfe] shadow-md' : 'border-[#e2e8f0] hover:border-[#bfdbfe]'}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left transition-colors bg-[#1d4ed8] hover:bg-[#1e40af]"
      >
        <span className="text-sm font-semibold leading-snug text-white">
          {item.q}
        </span>
        <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors bg-white/20">
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`w-3.5 h-3.5 transition-transform text-white ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>
      {isOpen && (
        <div className="px-5 pb-4 text-sm text-[#475569] leading-relaxed border-t border-[#e2e8f0] pt-3 bg-[#fafcff]">
          {item.a}
        </div>
      )}
    </div>
  );
}

export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="py-24 bg-white">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12 space-y-4">
          <div className="inline-flex items-center gap-2 bg-[#eff6ff] border border-[#bfdbfe] rounded-full px-4 py-1.5 text-xs font-semibold text-[#1d4ed8]">
            Got questions?
          </div>
          <h2 className="text-4xl font-extrabold text-[#0f172a] tracking-tight">Frequently asked questions</h2>
          <p className="text-[#475569]">
            Anything else?{' '}
            <a href="mailto:contact@apporb.net" className="text-[#1d4ed8] hover:underline font-medium">
              Email us
            </a>{' '}
            — we typically reply within a few hours.
          </p>
        </div>

        {/* FAQ list */}
        <div className="space-y-3">
          {FAQS.map((item, i) => (
            <FAQItem
              key={i}
              item={item}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>

        {/* Still have questions CTA */}
        <div
          className="mt-12 rounded-2xl p-8 text-center space-y-4"
          style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', border: '1px solid #bfdbfe' }}
        >
          <div className="w-12 h-12 mx-auto rounded-xl bg-white shadow-sm flex items-center justify-center">
          <FontAwesomeIcon icon={faEnvelope} className="w-6 h-6 text-[#1d4ed8]" />
          </div>
          <h3 className="text-lg font-bold text-[#0f172a]">Still have questions?</h3>
          <p className="text-sm text-[#475569]">
            Our team is happy to walk you through Apporb, help with onboarding, or discuss a custom plan.
          </p>
          <a
            href="mailto:contact@apporb.net"
            className="inline-flex items-center gap-2 px-6 py-3 text-white text-sm font-bold rounded-xl transition-all hover:-translate-y-0.5 hover:shadow-lg shadow-md"
            style={{ background: 'linear-gradient(135deg, #1d4ed8, #0284c7)' }}
          >
            Get in touch
            <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
